import { ChangeDetectionStrategy, Component, OnInit } from '@angular/core';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';

import { OnDestroyMixin, untilComponentDestroyed } from '@w11k/ngx-componentdestroyed';
import { TsPageSidenavConfig } from 'tsui/lib/ts-page/ts-page.component';
import { SavedView } from 'app/core/models';
import UrlUtils from 'app/shared/Utils/url.utils';
import { RelatedToType } from 'app/shared/enums';
import { SavedViewsSidebarService } from 'app/shared/services/saved-views-sidebar.service';
import { AccountState } from '@state/account/state';
import { FeatureFlag, NewPermission } from '@core/enums';
import { UserState } from '@state/user/state';
import { Store } from '@ngxs/store';

@Component({
  selector: 'aggregated-tasks',
  templateUrl: './aggregated-tasks.component.html',
  styleUrls: ['./aggregated-tasks.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AggregatedTasksComponent extends OnDestroyMixin implements OnInit {
  private savedViews: SavedView[];
  public savedViewsSidebarConfig: TsPageSidenavConfig;
  public relatedToType = RelatedToType;

  constructor(
    private readonly activatedRoute: ActivatedRoute,
    private readonly router: Router,
    private readonly savedViewsSidebarService: SavedViewsSidebarService,
    private readonly store: Store,
  ) {
    super();
  }

  ngOnInit(): void {
    this.activatedRoute.data.pipe(untilComponentDestroyed(this)).subscribe((data) => {
      this.savedViews = data.savedViews.filter(
        (savedView: SavedView) =>
          savedView.related_to_type === this.relatedToType.AggregatedTasks &&
          (this.store.selectSnapshot(AccountState.isFeatureEnabled(FeatureFlag.NEW_PERMISSIONS))
            ? savedView.permissions.is_viewable
            : true),
      );
      this.checkRedirectToFirstObjectView(this.activatedRoute);
    });

    this.router.events.pipe(untilComponentDestroyed(this)).subscribe((evt) => {
      if (evt instanceof NavigationEnd) {
        this.checkRedirectToFirstObjectView(this.activatedRoute);
      }
    });

    this.savedViewsSidebarConfig = this.savedViewsSidebarService.getSidenavConfig(this.relatedToType.AggregatedTasks);
  }

  private checkRedirectToFirstObjectView(route: ActivatedRoute): void {
    if (!route.snapshot.firstChild && this.savedViews.length > 0) {
      let savedViewId = UrlUtils.getFromLocalStorage('aggregatedTasksSavedViewId');
      const savedView = this.savedViews.find((view) => view.id === savedViewId);
      if (savedViewId && savedView) {
        this.router.navigate(['./', savedViewId, 'table'], { relativeTo: route, queryParamsHandling: 'merge' });
      } else {
        this.router.navigate(['./', this.savedViews[0].id, 'table'], {
          relativeTo: route,
          queryParamsHandling: 'merge',
        });
      }
    }
  }

  canViewAggregatedTasks(): boolean {
    if (!this.store.selectSnapshot(AccountState.isFeatureEnabled(FeatureFlag.NEW_PERMISSIONS))) {
      return true;
    }

    return this.store.selectSnapshot(UserState.hasPermission(NewPermission.AGGREGATED_TASK_LISTS_VIEW));
  }
}
